import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

const TripForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    driverId: '',
    vehicleId: '',
    startedAt: '',
    label: '',
    type: '',
    endedAt: '',
    deviceIds: []
  });

  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    // Fetch active drivers
    setLoadingDrivers(true);
    const driversRes = await get('/api/drivers/filter?active=true&size=100');
    if (driversRes.data) {
      setDrivers(driversRes.data.content || []);
      console.log('Drivers loaded:', driversRes.data.content);
    }
    setLoadingDrivers(false);

    // Fetch active vehicles
    setLoadingVehicles(true);
    const vehiclesRes = await get('/api/vehicles/filter?active=true&size=100');
    if (vehiclesRes.data) {
      setVehicles(vehiclesRes.data.content || []);
      console.log('Vehicles loaded:', vehiclesRes.data.content);
    }
    setLoadingVehicles(false);

    // Fetch active devices
    setLoadingDevices(true);
    const devicesRes = await get('/api/devices/filter?active=true&size=100');
    if (devicesRes.data) {
      setDevices(devicesRes.data.content || []);
      console.log('Devices loaded:', devicesRes.data.content);
    }
    setLoadingDevices(false);

    // Fetch trip data if editing
    if (id) {
      const tripRes = await get(`/api/trips/${id}`);
      if (tripRes.data) {
        setFormData({
          driverId: tripRes.data.driver?.id || '',
          vehicleId: tripRes.data.vehicle?.id || '',
          startedAt: tripRes.data.startedAt ? tripRes.data.startedAt.slice(0, 16) : '',
          label: tripRes.data.label || '',
          type: tripRes.data.type || '',
          endedAt: tripRes.data.endedAt ? tripRes.data.endedAt.slice(0, 16) : '',
          deviceIds: tripRes.data.devices?.map(d => d.id) || []
        });
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.driverId) {
      newErrors.driverId = 'Driver is required';
    }
    if (!formData.vehicleId) {
      newErrors.vehicleId = 'Vehicle is required';
    }
    if (!formData.startedAt) {
      newErrors.startedAt = 'Start time is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addDevice = () => {
    if (selectedDevice && !formData.deviceIds.includes(parseInt(selectedDevice))) {
      setFormData(prev => ({
        ...prev,
        deviceIds: [...prev.deviceIds, parseInt(selectedDevice)]
      }));
      setSelectedDevice('');
    }
  };

  const removeDevice = (deviceId) => {
    setFormData(prev => ({
      ...prev,
      deviceIds: prev.deviceIds.filter(id => id !== deviceId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      driverId: parseInt(formData.driverId),
      vehicleId: parseInt(formData.vehicleId),
      startedAt: formData.startedAt,
      label: formData.label || null,
      type: formData.type || null,
      endedAt: formData.endedAt || null,
      deviceIds: formData.deviceIds
    };

    console.log('Submitting trip data:', submitData);

    let response;
    if (id) {
      response = await put(`/api/trips/${id}`, submitData);
    } else {
      response = await post('/api/trips', submitData);
    }

    if (!response.error) {
      navigate('/trips/filter');
    }
  };

  if ((loading && id) || loadingDrivers || loadingVehicles || loadingDevices) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/trips/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit Trip' : 'Create New Trip'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Driver Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver <span className="text-red-500">*</span>
              </label>
              <select
                name="driverId"
                value={formData.driverId}
                onChange={handleChange}
                className={`input-field ${errors.driverId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a driver</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.fullName} - {driver.mobileNumber || 'No phone'} 
                    {driver.otherId && ` (ID: ${driver.otherId})`}
                  </option>
                ))}
              </select>
              {errors.driverId && (
                <p className="mt-1 text-sm text-red-600">{errors.driverId}</p>
              )}
              {drivers.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No active drivers found. Please create a driver first.</p>
              )}
            </div>

            {/* Vehicle Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="vehicleId"
                value={formData.vehicleId}
                onChange={handleChange}
                className={`input-field ${errors.vehicleId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.brand} - {vehicle.plates} ({vehicle.color})
                    {vehicle.other && ` - ${vehicle.other}`}
                  </option>
                ))}
              </select>
              {errors.vehicleId && (
                <p className="mt-1 text-sm text-red-600">{errors.vehicleId}</p>
              )}
              {vehicles.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No active vehicles found. Please create a vehicle first.</p>
              )}
            </div>

            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label
              </label>
              <input
                type="text"
                name="label"
                value={formData.label}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter trip label"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select trip type</option>
                <option value="DELIVERY">Delivery</option>
                <option value="TRANSPORT">Transport</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="TEST">Test</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Started At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Started At <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="startedAt"
                value={formData.startedAt}
                onChange={handleChange}
                className={`input-field ${errors.startedAt ? 'border-red-500' : ''}`}
              />
              {errors.startedAt && (
                <p className="mt-1 text-sm text-red-600">{errors.startedAt}</p>
              )}
            </div>

            {/* Ended At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ended At
              </label>
              <input
                type="datetime-local"
                name="endedAt"
                value={formData.endedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Devices Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Devices
              </label>
              <div className="flex space-x-2">
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  className="input-field flex-1"
                >
                  <option value="">Select a device</option>
                  {devices
                    .filter(device => !formData.deviceIds.includes(device.id))
                    .map(device => (
                      <option key={device.id} value={device.id}>
                        {device.brand} - {device.devType} 
                        {device.deviceType && ` (${device.deviceType.description})`}
                        {device.orientationType && ` - ${device.orientationType.description}`}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={addDevice}
                  disabled={!selectedDevice}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Device
                </button>
              </div>
              {devices.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No active devices found. Please create a device first.</p>
              )}

              {/* Selected Devices List */}
              {formData.deviceIds.length > 0 && (
                <div className="mt-3 space-y-2">
                  <h3 className="text-sm font-medium text-gray-700">Selected Devices:</h3>
                  {formData.deviceIds.map(deviceId => {
                    const device = devices.find(d => d.id === deviceId);
                    return (
                      <div key={deviceId} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div>
                          <span className="font-medium">{device?.brand}</span>
                          <span className="text-sm text-gray-600 ml-2">
                            {device?.devType} - ID: {deviceId}
                          </span>
                          {device?.deviceType && (
                            <span className="text-xs text-gray-500 block">
                              Type: {device.deviceType.description}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDevice(deviceId)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/trips/filter')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Trip' : 'Create Trip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripForm;